import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScrapingJobService } from '@/services/scraping-job.service';
import { format } from 'date-fns';

export const ScrapingJobs: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['scrapingJobs'],
    queryFn: () => ScrapingJobService.getScrapingJobs(),
    refetchInterval: (query) => {
      // If there are pending or running jobs, refetch every 5 seconds
      const jobs = query.state.data || [];
      const hasActiveJobs = jobs.some((job: any) => job.status === 'pending' || job.status === 'running');
      return hasActiveJobs ? 5000 : false;
    },
  });

  // Auto-refresh when jobs are pending/running
  useEffect(() => {
    const hasActiveJobs = jobs.some((job: any) => job.status === 'pending' || job.status === 'running');
    if (hasActiveJobs) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['scrapingJobs'] });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [jobs, queryClient]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      running: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/sales')}
          className="text-blue-600 hover:text-blue-700 text-sm mb-4"
        >
          ← Back to Sales
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Scraping Jobs</h1>
            <p className="text-gray-600 mt-1">Monitor automated scraping jobs</p>
          </div>
          <button
            onClick={() => navigate('/sales/scrape')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            + New Scraping Job
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-600">No scraping jobs yet</p>
          <button
            onClick={() => navigate('/sales/scrape')}
            className="mt-4 inline-block text-blue-600 hover:text-blue-700"
          >
            Create your first scraping job
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Search</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Results</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {job.searchQuery}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.location}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.source === 'both' ? 'Google Maps + Yelp' : job.source}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.resultsCount || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(job.createdAt, 'MMM d, yyyy h:mm a')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {(job.status === 'pending' || job.status === 'running') && (
                      <button
                        onClick={() => navigate(`/sales/scraping-jobs/${job.id}`)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        View Status
                      </button>
                    )}
                    {job.status === 'completed' && (
                      <button
                        onClick={() => navigate('/sales/approval-queue')}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        View Results
                      </button>
                    )}
                    {job.status === 'failed' && (
                      <button
                        onClick={() => navigate(`/sales/scraping-jobs/${job.id}`)}
                        className="text-red-600 hover:text-red-700"
                      >
                        View Error
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

